/**
 * syncQueue.ts
 *
 * Client-side sync orchestration (Safari / Firefox fallback path).
 * Chrome / Edge use the Service Worker Background Sync API directly.
 *
 * Hardening:
 *  - Mutex: prevents double-sync when online event fires multiple times
 *  - Jitter: random delay so all users reconnecting after an outage don't
 *    hammer the DB simultaneously (thundering herd protection)
 *  - Batching: BATCH_SIZE mutations processed in parallel (speed vs load balance)
 *  - Idempotency: CREATE with clientId → 409 treated as success (already exists)
 *  - Conflict via HTTP 412: PUT with If-Unmodified-Since → single request,
 *    no separate GET needed before PUT
 */

import { toast } from 'react-toastify';
import fetchClient from 'interfaces/fetchClient';
import { offlineDb } from 'utils/offlineDb';

const BATCH_SIZE = 3; // max concurrent mutations
// Module-level mutex — prevents double-sync from concurrent online events
let __syncInProgress = false;

// ─── Background Sync registration ─────────────────────────────────────────────

/**
 * Register a Background Sync tag with the active Service Worker (Chrome/Edge).
 * Falls back to direct processMutations() on Safari/Firefox.
 */
export const registerBackgroundSync = async (): Promise<void> => {
    try {
        if ('serviceWorker' in navigator && 'SyncManager' in window) {
            const reg = await navigator.serviceWorker.ready;
            await (reg as any).sync.register('sync-mutations');
            // BG sync will handle it — no need to run directly here
        } else {
            await processMutations();
        }
    } catch {
        await processMutations();
    }
};

// ─── Mutation processing (Safari/Firefox direct path) ─────────────────────────

interface MutationResult {
    status: 'synced' | 'conflict' | 'failed';
    conflictData?: { serverVersion: any; mutation: any };
}

async function processSingleMutation(mutation: any): Promise<MutationResult> {
    const token = mutation.token || localStorage.getItem('token') || '';
    const authHeader = `Bearer ${token}`;

    try {
        if (mutation.type === 'CREATE_ORDER') {
            // Idempotency key lives outside the body (header only). Strip it from
            // the payload defensively so the strict backend validator never sees it.
            const idempotencyKey = mutation.clientId || mutation.payload?.clientId;
            const body = { ...mutation.payload };
            delete body.clientId;
            try {
                await fetchClient.post('/orders', body, {
                    headers: {
                        Authorization: authHeader,
                        // Idempotency key — server discards duplicate POSTs with same key
                        ...(idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {}),
                    },
                });
                // 200/201 = created
                return { status: 'synced' };
            } catch (err: any) {
                // Idempotent duplicate: already exists on server
                const status = err?.response?.status ?? err?.status;
                if (status === 409) return { status: 'synced' };
                throw err;
            }
        }

        if (mutation.type === 'UPDATE_ORDER') {
            const orderId = mutation.payload._id;
            const body = { ...mutation.payload };
            delete body._id;

            // Single-request conflict detection via If-Unmodified-Since.
            // A 412 from the server means the record was modified after our edit.
            const extraHeaders: Record<string, string> = {};
            if (mutation.serverUpdatedAt) {
                extraHeaders['If-Unmodified-Since'] = new Date(mutation.serverUpdatedAt).toUTCString();
            }

            try {
                const res = await fetchClient.put(`/orders/${orderId}`, body, {
                    headers: { Authorization: authHeader, ...extraHeaders },
                });
               if (res && res.status >= 200 && res.status < 300) return { status: 'synced' };
            } catch (err: any) {
                // fetchClient throws on 4xx/5xx — check if it is a 412
                const status = err?.response?.status ?? err?.status;
                if (status === 412) {
                    const serverVersion = err?.response?.data ?? null;
                    // Fetch current server version for the conflict UI if not in response
                    let resolved = serverVersion;
                    if (!resolved?._id) {
                        try {
                            const check = await fetchClient.get(`/orders/${orderId}`, {
                                headers: { Authorization: authHeader },
                            });
                            resolved = check?.data ?? null;
                        } catch { /* leave null */ }
                    }
                    return { status: 'conflict', conflictData: { serverVersion: resolved, mutation } };
                }
                throw err; // re-throw non-412 errors as failed
            }
        }
    } catch {
        return { status: 'failed' };
    }
    return { status: 'failed' };
}

/**
 * Replay all queued mutations.
 * Called as the direct fallback when Background Sync is unavailable.
 */
export const processMutations = async (): Promise<void> => {
    if (__syncInProgress) return; // mutex: skip if already running
    __syncInProgress = true;

    try {
        // Jitter: random 0–4s delay to spread load when many users reconnect at once
        await new Promise<void>((r) => setTimeout(r, Math.random() * 4000));

        const mutations = await offlineDb.getPendingMutations();
        if (mutations.length === 0) return;

        toast.info(
            `🔄 Synchronisation de ${mutations.length} modification${mutations.length > 1 ? 's' : ''} en attente…`,
            { autoClose: 3000, toastId: 'sync-progress' },
        );

        let syncedCount = 0;
        let conflictCount = 0;

        // Process in parallel batches of BATCH_SIZE
        for (let i = 0; i < mutations.length; i += BATCH_SIZE) {
            const chunk = mutations.slice(i, i + BATCH_SIZE);
            const results = await Promise.all(chunk.map(processSingleMutation));

            for (let j = 0; j < results.length; j++) {
                const r = results[j];
                const mutation = chunk[j];

                if (r.status === 'synced') {
                    if (mutation.id !== undefined)
                        await offlineDb.deletePendingMutation(mutation.id as number);
                    syncedCount++;
                } else if (r.status === 'conflict' && r.conflictData) {
                    await offlineDb.addConflict({
                        orderId: mutation.payload._id,
                        localVersion: mutation.payload,
                        serverVersion: r.conflictData.serverVersion,
                        mutation,
                        detectedAt: Date.now(),
                        status: 'pending',
                    });
                    if (mutation.id !== undefined)
                        await offlineDb.deletePendingMutation(mutation.id as number);
                    conflictCount++;
                }
            }
        }

        if (syncedCount > 0)
            toast.success(`✅ ${syncedCount} change${syncedCount > 1 ? 's' : ''} synced!`);

        if (conflictCount > 0) {
            toast.warn(
                `⚠️ ${conflictCount} conflict${conflictCount > 1 ? 's' : ''} — please review.`,
                { autoClose: false },
            );
            window.dispatchEvent(new CustomEvent('toola:sync-conflict'));
        }

        const remaining = mutations.length - syncedCount - conflictCount;
        if (remaining > 0)
            toast.error(`⛔ ${remaining} change${remaining > 1 ? 's' : ''} failed — will retry.`);
    } finally {
        __syncInProgress = false;
    }
};

// Alias kept for any legacy imports
export const processSyncQueue = processMutations;
